import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement } from 'lit/decorators.js';
import { styles } from '../view/styles';

export interface PhasrListItem {
  id: string;
  name: string;
  description: string | (() => TemplateResult);
  icon?: string | (() => TemplateResult);
}

/**
 * @summary This component is used to display a single phaser list item, inside the 'gz-phasr-list' component
 * @tag gz-phasr-list-item
 * @property {string} id - the unique identifier of the item
 * @property {string} name - the name of the item
 * @property {string | TemplateResult} description - the description of the item, displayed below the name. Alternatively, a function that returns a TemplateResult can be provided, if we need to display custom html content
 * @property {string | TemplateResult} icon - the icon to display next to the name. Default is 'ds-icon-resource_user_role' in 'secondary (grey)' color, can be replaced with any other icon if needed
 */
@customElement('gz-phasr-list-item')
export class PhasrListItemWebComponent extends LitElement implements PhasrListItem {
  @property({ type: String, attribute: true, reflect: true })
  id = '';

  @property({ type: String, attribute: true, reflect: true })
  name = '';

  /**
   * The description of the item, displayed below the name.
   * Alternatively, a function that returns a TemplateResult can be provided, if we need to display custom html content
   */
  @property({ type: String, attribute: true, reflect: true })
  description: string | (() => TemplateResult);

  /**
   * The icon to display next to the name. Default is 'ds-icon-resource_user_role', can be replaced with any other icon name if needed
   */
  @property({ type: String, attribute: true, reflect: true })
  icon: string | (() => TemplateResult);

  static get styles() {
    return styles;
  }

  render() {
    return html`
      <div class="list-item--wrapper">
        <div class="list-item--name">
          ${typeof this.icon === 'string'
            ? html`<gz-icon class="icon" .name=${this.icon} size="20" color="secondary"></gz-icon>`
            : this.icon()}
          <gz-text fontSize="12" fontWeight="500" lineHeight="1">${this.name}</gz-text>
        </div>
        ${typeof this.description === 'string'
          ? html`<gz-text class="list-item--description" fontSize="12" fontWeight="400">${this.description}</gz-text>`
          : html`<div class="list-item--description">${this.description()}</div>`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-phasr-list-item': PhasrListItemWebComponent;
  }
}
